import { APPLICATION_NAMES, type ApplicationName } from './enums/applicationNames';
import { AUTH_EVENTS } from './enums/analyticsEvents';

export type LogoutReason = 'user_initiated' | 'token_invalid' | 'password_reset';

// $analytics.track() already caps its own flush time (see app/plugins/amplitude.ts),
// so this just needs to guard against $analytics being unavailable or rejecting.
export async function trackAndFlush(eventName: string, props: Record<string, unknown>) {
  try {
    const { $analytics } = useNuxtApp();
    await $analytics?.track(eventName, props);
  } catch {
    // Analytics must never block or break the logout flow.
  }
}

export function trackLoggedOut(reason: LogoutReason, applicationName: ApplicationName = APPLICATION_NAMES.THRILLSHARE) {
  return trackAndFlush(AUTH_EVENTS.LOGGED_OUT, {
    application_name: applicationName,
    logout_reason: reason,
  });
}

export type AuthErrorContext = 'signin_callback' | 'silent_renew' | 'token_invalid';

const ERROR_MESSAGE_MAX_LENGTH = 250;

// oidc-client-ts throws ErrorResponse (carrying the OIDC protocol error code,
// e.g. invalid_grant / login_required, with error_description as the message),
// ErrorTimeout for iframe renew timeouts, and plain Errors for local failures
// such as "No matching state found in storage".
export function extractAuthErrorProps(error: unknown) {
  const err = error as { name?: string; message?: string; error?: string } | null;
  let errorType = err?.error || err?.name || 'unknown';
  if (!err?.error && err?.name === 'Error' && /\bstate\b/i.test(err?.message ?? '')) {
    errorType = 'state_mismatch';
  }
  return {
    error_type: errorType,
    error_message: err?.message?.slice(0, ERROR_MESSAGE_MAX_LENGTH) ?? '',
  };
}

export function trackAuthError(
  context: AuthErrorContext,
  error: unknown,
  applicationName: ApplicationName = APPLICATION_NAMES.THRILLSHARE,
  extra: Record<string, unknown> = {},
) {
  return trackAndFlush(AUTH_EVENTS.AUTH_ERROR, {
    application_name: applicationName,
    error_context: context,
    ...extractAuthErrorProps(error),
    ...extra,
  });
}
